import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const register = (name, email, password) =>
  api.post('/auth/register', { name, email, password });

export const login = (email, password, twoFactorToken) =>
  api.post('/auth/login', { email, password, twoFactorToken });

export const changePassword = (currentPassword, newPassword) =>
  api.put('/auth/password', { currentPassword, newPassword });

export const impersonateUser = (userId) =>
  api.post(`/auth/impersonate/${encodeURIComponent(userId)}`);

export const adminResetPassword = (userId) =>
  api.post(`/auth/admin-reset-password/${encodeURIComponent(userId)}`);

export const requestPasswordReset = (email) =>
  api.post('/auth/forgot-password', { email });

export const setupTwoFactor = () => api.post('/auth/2fa/setup');

export const enableTwoFactor = (token) => api.post('/auth/2fa/enable', { token });

export const getMe = () => api.get('/users/me');

export const updateEmergencyContact = (contact) =>
  api.put('/users/me/emergency-contact', contact);

export const getAllUsers = () => api.get('/users/users');

export const createPartner = (name, email) =>
  api.post('/users/users', { name, email });

export const deletePartner = (userId) =>
  api.delete(`/users/users/${encodeURIComponent(userId)}`);

export const exportPartnersCsv = () =>
  api.get('/users/users/export.csv', { responseType: 'blob' });

export const exportMyResultsCsv = () =>
  api.get('/users/me/export.csv', { responseType: 'blob' });

export const exportMyResultsExcel = () =>
  api.get('/users/me/export.xlsx', { responseType: 'blob' });

export const getFundUpdates = () => api.get('/updates');

export const createFundUpdate = (title, message, titleEn, messageEn) =>
  api.post('/updates', { title, message, titleEn, messageEn });

export const updateFundUpdate = (updateId, payload) =>
  api.put(`/updates/${encodeURIComponent(updateId)}`, payload);

export const deleteFundUpdate = (updateId) =>
  api.delete(`/updates/${encodeURIComponent(updateId)}`);

export const getAuditLogs = () => api.get('/audit');

export const getAdminStatus = () => api.get('/audit/status');

export const deleteResetRequest = (requestId) =>
  api.delete(`/audit/reset-requests/${encodeURIComponent(requestId)}`);

export const downloadBackup = () =>
  api.get('/audit/backup', { responseType: 'blob' });

export const getImportPeriods = () => api.get('/import/periods');

export const getImportArchive = () => api.get('/import/archive');

const excelFormData = (file, lastDate, statementDate) => {
  const formData = new FormData();
  formData.append('excel', file);
  if (lastDate) {
    formData.append('lastDate', lastDate);
  }
  if (statementDate) {
    formData.append('statementDate', statementDate);
  }

  return formData;
};

export const previewExcel = (file, lastDate, statementDate) =>
  api.post('/import/excel/preview', excelFormData(file, lastDate, statementDate));

export const uploadExcel = (file, lastDate, statementDate) =>
  api.post('/import/excel', excelFormData(file, lastDate, statementDate));

export const getInterimValuation = () => api.get('/import/interim');

export const previewInterimValuation = (payload) => api.post('/import/interim/preview', payload);

export const applyInterimValuation = (payload) => api.post('/import/interim', payload);

export const revertInterimValuation = () => api.delete('/import/interim');

export const getDocuments = () => api.get('/documents');

export const uploadDocument = (file) => {
  const formData = new FormData();
  formData.append('letter', file);
  return api.post('/documents', formData);
};

export const deleteDocument = (documentId) =>
  api.delete(`/documents/${encodeURIComponent(documentId)}`);

export const downloadDocument = (documentId) =>
  api.get(`/documents/${encodeURIComponent(documentId)}/download`, {
    responseType: 'blob'
  });

export default api;
